/**
 * VoiceCall — duplex v3 (one smart MAIN + one fast fill channel). The host owns STT, TTS and MAIN; VoiceCall owns only
 * the fast model. STT finals go through TurnGate (hold / merge / dedupe / one goodbye) and reach MAIN via `sendTurn`
 * with the user's words verbatim. The fill channel (SecondChannel + Progress) speaks backchannels, fillers, bridges and
 * progress lines through `speak`/`show` only: it has NO path to MAIN — its options carry no turn callback, and nothing
 * it produces is ever passed to `sendTurn`.
 *
 * Language: per turn, detectLanguage on the user's own words (or the last language when the words fit it); the fill
 * channel speaks only in that language and stays silent when it is unknown (never English by default). MAIN answers
 * in the user's language from its own prompt.
 */
import { forComponent } from '../logging';
import type { ChatLike } from '../llm';
import { latestClaude } from '../models';
import { ChannelBanks, detectLanguage, fastBankGenerator, fitsLanguage, primaryLang, type Gender } from './channelLang';
import { OverlapJudge, overlapAside, overlapModelFor, type OverlapVerdict } from './overlap';
import { Progress, fastProgressGenerator, type ProgressOptions } from './progress';
import { channelDecide, fastLineGenerator, SecondChannel, type ChannelMode, type LineKind, type SecondChannelOptions } from './secondChannel';
import { TurnGate, type GateDrop, type GateTurn, type TurnGateOptions } from './turnGate';

const log = forComponent('VoiceCall');

export type FillKind = LineKind | 'progress';
/** MAIN's stream as the host forwards it. `id` = the turn (sendTurn's id); absent → the current turn. */
export interface MainEvent {
  type: 'start' | 'text' | 'tool' | 'tool_done' | 'audio' | 'done' | 'error';
  id?: string;
  text?: string;
  name?: string;
  args?: unknown;
}
export interface CallMetric {
  name: 'first_sound' | 'first_word' | 'turn' | 'drop' | 'hold' | 'fill' | 'lang';
  id?: string;
  /** ms from the user's final (the first final of a held/merged turn) */
  ms?: number;
  by?: 'fill' | 'progress' | 'main';
  kind?: string;
  text?: string;
  lang?: string | null;
}

export class VoiceCallOptions {
  /** Host runs MAIN with exactly `text` (a replace: roll the `replaces` turn back first). */
  sendTurn: (text: string, o: { id: string; replaces?: string }) => void = () => {};
  /** Host aborts a running MAIN turn (barge-in). */
  abortTurn: (id: string) => void = () => {};
  /** Host TTS (fill lane): resolves when played; `false` = could not play. */
  speak: (text: string, kind: FillKind) => Promise<unknown> | boolean = () => false;
  /** Stop the in-flight fill line now (barge-in only). */
  cancel: () => void = () => {};
  /** Host caption — never history. */
  show: (text: string, kind: FillKind) => void = () => {};
  /** The fast model (lines, banks, progress) — used only with `ai`. */
  fastModel = latestClaude('haiku');
  /** Model client for the fast model + the channel decider / overlap judge. Absent → canned lines and rules only. */
  ai?: ChatLike;
  /** Cache dir for per-language banks (ChannelBanks). */
  bankCacheDir?: string;
  /** The host STT's language (BCP-47) — the starting language; each turn's own words still decide. */
  lang?: string;
  /** The voice's gender: generated fill/progress lines speak of themselves in it (Hebrew templates too). '' = neutral. */
  gender: Gender = '';
  aec = false;
  mode: ChannelMode = 'normal';
  gate: Partial<TurnGateOptions> = {};
  progress: Partial<ProgressOptions> = {};
  /** Extra SecondChannel options (tests, tuning). speak/show/cancel are always VoiceCall's own. */
  channel: Partial<SecondChannelOptions> = {};
  onMetric: (m: CallMetric) => void = () => {};
  now: () => number = () => performance.now();
  /** An aside (aside()) needs this long since the last one — a cue answered every time sounds scripted. */
  asideGapMs = 8000;
}

interface Turn { id: string; text: string; at: number; running: boolean; streamed: boolean; sounded: boolean; worded: boolean }

export class VoiceCall {
  readonly fill: SecondChannel;
  readonly gate: TurnGate;
  readonly progress: Progress;
  private judge: OverlapJudge;
  private cur?: Turn;
  private lang: string | null = null;
  private lastAside = { text: '', at: -Infinity };
  private lastAudibleAt = -Infinity;
  private firstFinalAt: number | null = null; // the first final of the turn being formed (hold/merge)
  private progressTimer: ReturnType<typeof setTimeout> | null = null;
  private ended = false;

  public constructor(public options?: Partial<VoiceCallOptions>) {
    this.options = { ...new VoiceCallOptions(), ...options };
    const o = this.o;
    if (o.lang) this.lang = primaryLang(o.lang);
    const ai = o.ai;
    // The fill channel: speak/show/cancel only (no turn callback exists on it — no path to MAIN).
    this.fill = new SecondChannel({
      ...(ai ? {
        banks: new ChannelBanks({ generate: fastBankGenerator(ai, o.fastModel), ...(o.bankCacheDir ? { cacheDir: o.bankCacheDir } : {}) }),
        decide: channelDecide(ai),
        generate: fastLineGenerator(ai, o.fastModel, o.gender),
      } : {}),
      mode: o.mode,
      aec: o.aec,
      bridgeDelayMs: 700, instantBridge: true, // v3 §3: first sound <1.2s p50 — a bridge at 700ms if MAIN is still silent, no veto, no wait
      ...(this.lang ? { lang: this.lang } : {}),
      ...o.channel,
      speak: (text, kind) => this.say(text, kind),
      show: (text, kind) => o.show(text, kind),
      cancel: () => o.cancel(),
      now: o.now,
    });
    this.progress = new Progress({ ...(ai ? { generate: fastProgressGenerator(ai, o.fastModel, o.gender) } : {}), gender: o.gender, now: o.now, ...o.progress });
    this.judge = new OverlapJudge({ ...(ai ? { model: overlapModelFor(ai) } : {}), now: o.now });
    this.gate = new TurnGate({
      now: o.now,
      ...o.gate,
      onTurn: (t) => this.onTurn(t),
      onDrop: (text, why) => this.onDrop(text, why),
      onHold: (text) => o.onMetric({ name: 'hold', text }),
    });
  }
  private get o(): VoiceCallOptions { return this.options as VoiceCallOptions; }

  /** The language the fill channel speaks right now (null = unknown → silent). */
  get language(): string | null { return this.lang; }
  /** The MAIN turn in flight (or the last one). */
  get turn(): { id: string; text: string; running: boolean } | undefined { return this.cur && { id: this.cur.id, text: this.cur.text, running: this.cur.running }; }

  /** The host STT's language (BCP-47): the current language until the user's words say otherwise. */
  setLanguage(tag: string): void { const l = primaryLang(tag); if (!l) return; this.lang = l; this.fill.setLanguage(l, true); }

  /** STT partial (the whole partial). */
  interim(text: string, _atMs?: number, aec?: boolean): void {
    if (this.ended || !text.trim()) return;
    if (typeof aec === 'boolean') this.fill.options!.aec = aec;
    this.gate.speaking();
    this.follow(text);
    this.fill.onInterim(text);
  }

  /** STT final → TurnGate. */
  final(text: string, sttId?: string): void {
    if (this.ended) return;
    this.firstFinalAt ??= this.o.now();
    this.gate.final(text, sttId);
  }

  /** A v2 host that decides turns itself: the user's line already went to MAIN (no TurnGate, no sendTurn) — the fill
   *  channel follows its language and covers the wait. */
  hostTurn(text: string): void { if (this.ended || !text.trim()) return; this.follow(text); this.fill.onUserTurn(text); }

  pause(ms: number): void { if (!this.ended) this.fill.onPause(ms); }

  /** MAIN's stream. */
  main(ev: MainEvent): void {
    const c = this.cur;
    if (!c || (ev.id && ev.id !== c.id)) return; // a replaced/aborted turn's late events
    switch (ev.type) {
      case 'start': c.running = true; return;
      case 'text':
        if (!ev.text) return;
        if (!c.streamed) { c.streamed = true; this.gate.replyStarted(c.id); this.progress.text(); this.clearProgress(); }
        this.fill.onMainTokens(ev.text);
        return;
      case 'tool':
        this.gate.replyStarted(c.id);
        void this.onTool(c, ev.name ?? '', ev.args);
        return;
      case 'tool_done': return;
      case 'audio':
        this.lastAudibleAt = this.o.now();
        this.fill.onAgentState('first_audio');
        this.sounded(c, 'main');
        if (!c.worded) { c.worded = true; this.o.onMetric({ name: 'first_word', id: c.id, ms: Math.round(this.o.now() - c.at), by: 'main' }); }
        return;
      case 'done': case 'error':
        c.running = false;
        this.clearProgress();
        this.fill.onAgentState('idle');
        return;
    }
  }

  /** The user barged in (hard): cut fill + MAIN audio (the host cuts its own), abort a running MAIN turn. */
  barge(): void {
    this.fill.onBargeIn();
    this.clearProgress();
    try { this.o.cancel(); } catch (e) { log.warn(`cancel threw: ${(e as Error).message}`); }
    if (this.cur?.running) { this.cur.running = false; this.o.abortTurn(this.cur.id); }
  }

  /** Soft barge-in: keep (a listener cue — no turn) or cut (→ barge()). */
  async overlap(text: string): Promise<OverlapVerdict> {
    const d = await this.judge.judge(text);
    if (d.verdict === 'cut') this.barge();
    return d.verdict;
  }

  /** The fill channel's spoken aside for an overlap judged 'keep' ("yeah" → "Oh yeah, definitely."): the host plays it
   *  while the reply is held, then resumes the same reply (bod-voice decideOverlap's aside). ≤4 canned words, never a
   *  claim, the cue's language; null = stay quiet (a plain listener cue, too soon after the last, or no line). It is a
   *  fill line: never MAIN history. */
  aside(text: string): string | null {
    const now = this.o.now();
    if (now - this.lastAside.at < this.o.asideGapMs) return null;
    const line = overlapAside(text, this.lang, this.lastAside.text);
    if (!line) return null;
    this.lastAside = { text: line, at: now };
    this.o.onMetric({ name: 'fill', kind: 'aside', text: line, lang: this.lang });
    return line;
  }

  /** Did the last barge cut OUR fill line `text`? (hosts strip it from the cut reply — it is not MAIN's speech) */
  spoke(text: string): boolean { return this.fill.spoke(text); }
  /** Resolves when the in-flight fill line finished — hosts queue MAIN audio behind it (stack, don't cut). */
  drain(): Promise<void> { return this.fill.drain(); }

  end(): void {
    this.ended = true;
    this.gate.end();
    this.clearProgress();
  }

  // ---- internals ----

  /** Follow the user's language from their own words: a confident detection, else the current language if the words
   *  fit it, else unknown (silent). Pinned into the channel so its own detection never diverges. */
  private follow(text: string): void {
    const l = detectLanguage(text) ?? (this.lang && fitsLanguage(text, this.lang) ? this.lang : null);
    if (l !== this.lang) this.o.onMetric({ name: 'lang', lang: l, text });
    this.lang = l;
    if (l) this.fill.setLanguage(l, true);
  }

  private onTurn(t: GateTurn): void {
    const at = this.firstFinalAt ?? this.o.now();
    this.firstFinalAt = null;
    this.follow(t.text);
    this.clearProgress();
    this.cur = { id: t.id, text: t.text, at, running: true, streamed: false, sounded: false, worded: false };
    this.progress.turn();
    this.o.onMetric({ name: 'turn', id: t.id, kind: t.kind, text: t.text });
    this.o.sendTurn(t.text, { id: t.id, ...(t.replaces ? { replaces: t.replaces } : {}) });
    if (t.kind === 'new') this.fill.onUserTurn(t.text); // a replace/extend keeps the bridge already armed (no second one)
  }

  private onDrop(text: string, why: GateDrop): void {
    if (!this.gate.holding) this.firstFinalAt = null;
    this.o.onMetric({ name: 'drop', kind: why, text });
  }

  /** Every fill/progress line passes here: only in the turn's known language, never after the call ended. */
  private say(text: string, kind: FillKind): Promise<unknown> | boolean {
    if (this.ended || !this.lang || this.fill.language !== this.lang) return false;
    let r: Promise<unknown> | boolean;
    try { r = this.o.speak(text, kind); } catch (e) { log.warn(`speak threw: ${(e as Error).message}`); return false; }
    if (r === false) return false;
    this.lastAudibleAt = this.o.now();
    this.o.onMetric({ name: 'fill', kind, text, lang: this.lang });
    if (this.cur && (kind === 'bridge' || kind === 'progress')) this.sounded(this.cur, kind === 'progress' ? 'progress' : 'fill');
    else if (this.cur && this.cur.running && !this.cur.sounded && kind === 'filler') this.sounded(this.cur, 'fill');
    return r;
  }

  private sounded(c: Turn, by: CallMetric['by']): void {
    if (c.sounded) return;
    c.sounded = true;
    this.o.onMetric({ name: 'first_sound', id: c.id, ms: Math.round(this.o.now() - c.at), by });
  }

  private async onTool(c: Turn, name: string, args: unknown): Promise<void> {
    const lang = this.lang;
    if (!lang || c.streamed) return;
    const line = await this.progress.line(name, args, lang);
    if (!line || this.cur !== c || c.streamed || !c.running || this.lang !== lang) return;
    this.clearProgress();
    const tryNow = () => {
      this.progressTimer = null;
      if (this.ended || this.cur !== c || c.streamed || !c.running) return;
      if (!this.progress.due(this.lastAudibleAt)) { this.progressTimer = setTimeout(tryNow, 250); return; }
      if (this.say(line, 'progress') !== false) { this.progress.spoke(line); this.o.show(line, 'progress'); }
    };
    tryNow();
  }

  private clearProgress(): void { if (this.progressTimer) { clearTimeout(this.progressTimer); this.progressTimer = null; } }
}
